"use client"

import { useState, useEffect, useMemo } from "react"
import { cn } from "@/lib/utils"
import { makeHeadingIds } from "@/lib/heading-ids"

interface TOCItem {
  id: string
  text: string
  level: number
}

interface TableOfContentsProps {
  content: string
  className?: string
}

// Extract headings from markdown content, in document order. Ids are
// assigned via makeHeadingIds (shared with app/blog/[slug]/page.tsx) so
// repeated heading text gets unique -2/-3 suffixes instead of colliding.
// Levels 2-3 only: the converter drops a post's leading `# ` line (it
// duplicated the page <h1>), so the TOC must not link to it either.
function extractHeadings(content: string): TOCItem[] {
  const headingRegex = /^(#{2,3})\s+(.+)$/gm
  const raw: { level: number; text: string }[] = []
  let match

  while ((match = headingRegex.exec(content)) !== null) {
    raw.push({ level: match[1].length, text: match[2].trim() })
  }

  const ids = makeHeadingIds(raw)
  return raw.map((h, i) => ({ id: ids[i], text: h.text, level: h.level }))
}

export function TableOfContents({ content, className }: TableOfContentsProps) {
  const [activeId, setActiveId] = useState<string>("")
  const headings = useMemo(() => extractHeadings(content), [content])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id)
          }
        })
      },
      { rootMargin: "-80px 0px -80% 0px" }
    )

    headings.forEach(({ id }) => {
      const element = document.getElementById(id)
      if (element) observer.observe(element)
    })

    return () => observer.disconnect()
  }, [headings])

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const element = document.getElementById(id)
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      window.scrollTo({
        top: offsetPosition,
        behavior: prefersReducedMotion ? "auto" : "smooth"
      })
    }
  }

  if (headings.length === 0) return null

  return (
    <nav className={cn("space-y-1", className)}>
      <p className="tls-eyebrow mb-3">
        On this page
      </p>
      <ul className="space-y-1">
        {headings.map(({ id, text, level }) => (
          <li key={id}>
            <a
              href={`#${id}`}
              onClick={(e) => handleClick(e, id)}
              className={cn(
                "tlsr-focus block border-l-2 border-transparent text-sm py-1 pl-2 transition-[background-color,color,border-color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
                level === 1 && "pl-2",
                level === 2 && "pl-5",
                level === 3 && "pl-8",
                // Active section is marked by weight AND a 2px accent rule,
                // never colour alone (§06.2).
                activeId === id
                  ? "border-[color:var(--tls-brand-ink)] text-[var(--tls-brand-ink)] font-medium"
                  : "text-[var(--tls-fg-subtle)] hover:text-[var(--tls-fg-muted)] hover:bg-[var(--tls-surface-hover)]"
              )}
            >
              {text}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  )
}

// Mobile TOC that shows as a collapsible dropdown
export function MobileTableOfContents({ content, className }: TableOfContentsProps) {
  const [isOpen, setIsOpen] = useState(false)
  const headings = useMemo(() => extractHeadings(content), [content])

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    setIsOpen(false)
    const element = document.getElementById(id)
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.scrollY - offset

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      window.scrollTo({
        top: offsetPosition,
        behavior: prefersReducedMotion ? "auto" : "smooth"
      })
    }
  }

  if (headings.length === 0) return null

  return (
    <div className={cn("lg:hidden", className)}>
      <button type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-[var(--tls-fg-muted)] bg-[var(--tls-surface-elevated)] border border-[var(--tls-border-subtle)] rounded-[var(--ls-radius-0)] hover:bg-[var(--tls-surface-hover)] transition-[background-color,border-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]"
      >
        <span>On this page</span>
        <svg
          className={cn("size-4 transition-transform", isOpen && "rotate-180")}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <ul className="mt-2 p-2 bg-[var(--tls-surface-elevated)] border border-[var(--tls-border-subtle)] rounded-[var(--ls-radius-0)] space-y-1">
          {headings.map(({ id, text, level }) => (
            <li key={id}>
              <a
                href={`#${id}`}
                onClick={(e) => handleClick(e, id)}
                className={cn(
                  "tlsr-focus block text-sm py-2 px-3 rounded-[var(--ls-radius-0)] text-[var(--tls-fg-muted)] hover:text-[var(--tls-fg-strong)] hover:bg-[var(--tls-surface-hover)] transition-[background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)]",
                  level === 3 && "pl-6"
                )}
              >
                {text}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
